import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { fetchPropertyById } from "../services/api";
import { formatPrice } from "../utils/format";
import { Helmet } from "react-helmet";
import InspectionRequestModal from "../components/InspectionRequestModal";
import { formatUrl } from "../utils/config";
import { stripHtml } from "../utils/text";

const PropertyDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [property, setProperty] = useState(location.state?.property);
  const [loading, setLoading] = useState(!location.state?.property);
  const [mainImage, setMainImage] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [imageError, setImageError] = useState(false);
  const [showInspectionModal, setShowInspectionModal] = useState(false);

  useEffect(() => {
    const loadProperty = async () => {
      if (!property) {
        try {
          const data = await fetchPropertyById(id);
          setProperty(data);
          setMainImage(data.thumbnail);
        } catch (error) {
          console.error("Error loading property:", error);
          navigate("/properties", { replace: true });
        } finally {
          setLoading(false);
        }
      } else {
        setMainImage(property.thumbnail);
      }
    };

    loadProperty();
  }, [id, property, navigate]);

  if (loading) {
    return (
      <div className="space-top space-extra-bottom">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="space-top space-extra-bottom">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5">
              <h3>Property not found</h3>
              <p>
                The property you are looking for does not exist or has been
                removed.
              </p>
              <button
                className="th-btn"
                onClick={() => navigate("/properties")}
              >
                Back to Properties
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Create a safe HTML string for the features
  const createMarkup = (html) => {
    return { __html: html };
  };

  return (
    <>
      <Helmet>
        <meta name="description" content={property.description} />
      </Helmet>
      <div
        className="breadcumb-wrapper"
        style={{
          backgroundImage: "url('/assets/img/main/m1.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container">
          <div className="breadcumb-content">
            <h1 className="breadcumb-title">{`${property.houseNumber} - ${property.buildingType} - ${property.estate.name}`}</h1>
            <ul className="breadcumb-menu">
              <li>
                <Link to="/properties">Properties</Link>
              </li>
              <li>{`${property.houseNumber} - ${property.buildingType} - ${property.estate.name}`}</li>
            </ul>
          </div>
        </div>
      </div>

      <section className="space-top space-extra-bottom">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-8">
              <div className="property-main-image mb-4">
                <img
                  src={formatUrl(mainImage)}
                  alt={`${property.houseNumber} - ${property.buildingType}`}
                  className="img-fluid rounded w-100"
                  style={{ height: "500px", objectFit: "cover" }}
                />
              </div>

              {property.photos && property.photos.length > 0 && (
                <div className="property-thumbnails mb-5">
                  <div className="row g-3">
                    <div className="col-auto">
                      <button
                        className={`thumbnail-btn ${
                          mainImage === property.thumbnail ? "active" : ""
                        }`}
                        onClick={() => setMainImage(property.thumbnail)}
                      >
                        <img
                          src={formatUrl(property.thumbnail)}
                          alt="Thumbnail"
                          className="img-fluid"
                          style={{
                            width: "80px",
                            height: "60px",
                            objectFit: "cover",
                          }}
                        />
                      </button>
                    </div>
                    {property.photos.map((photo, index) => (
                      <div className="col-auto" key={index}>
                        <button
                          className={`thumbnail-btn ${
                            mainImage === photo ? "active" : ""
                          }`}
                          onClick={() => setMainImage(photo)}
                        >
                          <img
                            src={formatUrl(photo)}
                            alt={`Thumbnail ${index + 1}`}
                            className="img-fluid"
                            style={{
                              width: "80px",
                              height: "60px",
                              objectFit: "cover",
                            }}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="d-flex my-3">
                {(property?.design || property.estate.design) && (
                  <div className="estate-thumbnail flex-fill">
                    <video
                      width={"100%"}
                      height={"200px"}
                      src={formatUrl(
                        property?.design || property.estate?.design
                      )}
                      controls={true}
                      volume={0}
                      autoPlay={true}
                    />
                  </div>
                )}

                {property?.coordinates && (
                  <div className="estate-thumbnail ps-2 flex-fill">
                    <iframe
                      src={property.coordinates}
                      width="100%"
                      height={"200px"}
                      style={{ border: "0" }}
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                )}
              </div>

              <div className="property-details mb-5">
                <h2 className="h3 mb-3">
                  {property.houseNumber} - {property.buildingType}
                </h2>
                <p className="text-muted mb-4">
                  <i className="fas fa-map-marker-alt text-primary me-2"></i>
                  {property.estate.name}, Block {property.blockNumber},{" "}
                  {property.lga}, {property.district}, {property.state}
                </p>

                <div className="property-features mb-4">
                  <div className="row g-3">
                    <div className="col-md-2 col-4">
                      <div className="feature-box text-center p-3 border rounded">
                        <i className="fas fa-bed fa-2x text-primary mb-2"></i>
                        <h6 className="mb-0">
                          {property.bedRooms} Bed
                          {property.bedRooms !== 1 ? "s" : ""}
                        </h6>
                      </div>
                    </div>
                    <div className="col-md-2 col-4">
                      <div className="feature-box text-center p-3 border rounded">
                        <i className="fas fa-toilet fa-2x text-primary mb-2"></i>
                        <h6 className="mb-0">
                          {property.toilets} Bath
                          {property.toilets !== 1 ? "s" : ""}
                        </h6>
                      </div>
                    </div>
                    {property.livingRoom > 0 && (
                      <div className="col-md-2 col-4">
                        <div className="feature-box text-center p-3 border rounded">
                          <i className="fas fa-couch fa-2x text-primary mb-2"></i>
                          <h6 className="mb-0">{property.livingRoom} Living</h6>
                        </div>
                      </div>
                    )}
                    {property.kitchen > 0 && (
                      <div className="col-md-2 col-4">
                        <div className="feature-box text-center p-3 border rounded">
                          <i className="fas fa-utensils fa-2x text-primary mb-2"></i>
                          <h6 className="mb-0">{property.kitchen} Kitchen</h6>
                        </div>
                      </div>
                    )}
                    {property.dianning > 0 && (
                      <div className="col-md-2 col-4">
                        <div className="feature-box text-center p-3 border rounded">
                          <i className="fas fa-utensils fa-2x text-primary mb-2"></i>
                          <h6 className="mb-0">{property.dianning} Dining</h6>
                        </div>
                      </div>
                    )}
                    <div className="col-md-2 col-4">
                      <div className="feature-box text-center p-3 border rounded">
                        <i className="fas fa-ruler-combined fa-2x text-primary mb-2"></i>
                        <h6 className="mb-0">{property.sizeSqm} sqm</h6>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="property-description mb-5">
                  <h4 className="h5 mb-3">Description</h4>
                  <p>{stripHtml(property.description)}</p>
                </div>

                {property.features && (
                  <div className="property-features-list mb-5">
                    <h4 className="h5 mb-3">Features</h4>
                    <div
                      className="row g-1"
                      dangerouslySetInnerHTML={createMarkup(property.features)}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="col-lg-4">
              <div className="property-sidebar">
                <div className="property-price-card bg-light p-4 rounded mb-4">
                  <h3 className="h4 mb-3">
                    {formatPrice(property.price)} -{" "}
                    {formatPrice(
                      Number(property.price) + Number(property.price / 3)
                    )}
                  </h3>
                  <p className="text-muted mb-4">
                    <i className="fas fa-info-circle me-2"></i>
                    {property.status === "AVAILABLE"
                      ? "Available"
                      : "Not Available"}
                  </p>

                  <div className="property-actions d-flex flex-column gap-3 mb-3">
                    <button
                      className="btn-reservation w-100"
                      disabled={property.status !== "AVAILABLE"}
                      onClick={() => setShowInspectionModal(true)}
                    >
                      <i className="fas fa-search me-2"></i>
                      {property.status === "AVAILABLE"
                        ? "Request Inspection"
                        : "Not Available"}
                    </button>
                    <Link
                      to={`/purchase/${property.id}?type=house`}
                      className="btn btn-contact w-100 text-decoration-none"
                    >
                      <i className="fas fa-file me-2"></i> Purchase Form
                    </Link>

                    <Link
                      to="/contact"
                      className="btn btn-primary w-100 text-decoration-none"
                    >
                      <i className="fas fa-envelope me-2"></i> Contact Us
                    </Link>

                    <Link
                      to="tel:+2347055558942"
                      className="btn btn-warning w-100 text-decoration-none"
                    >
                      <i className="fas fa-phone me-2"></i> Call Us
                    </Link>
                  </div>

                  <div className="property-meta mt-4 pt-3 border-top">
                    <ul className="list-unstyled mb-0">
                      <li className="d-flex justify-content-between py-2">
                        <span>House Number:</span>
                        <strong>{property.houseNumber}</strong>
                      </li>
                      <li className="d-flex justify-content-between py-2">
                        <span>Block Number:</span>
                        <strong>{property.blockNumber}</strong>
                      </li>
                      <li className="d-flex justify-content-between py-2">
                        <span>Estate:</span>
                        <strong>{property.estate.name}</strong>
                      </li>
                      <li className="d-flex justify-content-between py-2">
                        <span>Property Type:</span>
                        <strong>{property.type}</strong>
                      </li>

                      <li className="d-flex justify-content-between py-2">
                        <span>Acquisition Type:</span>
                        <strong>{property.acquisitionType || "N/A"}</strong>
                      </li>
                      <li className="d-flex justify-content-between py-2">
                        <span>Documentation Fee:</span>
                        <strong>
                          {property.documentationFee
                            ? `${formatPrice(property.documentationFee)}`
                            : "N/A"}
                        </strong>
                      </li>
                      <li className="d-flex justify-content-between py-2">
                        <span>Development Fee:</span>
                        <strong>
                          {property.developmentFee
                            ? `${formatPrice(property.developmentFee)}`
                            : "N/A"}
                        </strong>
                      </li>
                      <li className="d-flex justify-content-between py-2">
                        <span>Status:</span>
                        <strong
                          className={
                            property.status === "AVAILABLE"
                              ? "text-success"
                              : "text-danger"
                          }
                        >
                          {property.status}
                        </strong>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="estate-info bg-light p-4 rounded mb-4">
                  <h4 className="h5 mb-3">Estate Information</h4>
                  <div className="estate-thumbnail mb-3">
                    <img
                      src={formatUrl(property.estate.thumbnail)}
                      alt={property.estate.name}
                      className="img-fluid rounded"
                      style={{
                        height: "150px",
                        width: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                  <div className="row">
                    <div className="col">
                      <h5 className="h6">{property.estate.name}</h5>
                    </div>
                    <div className="col">
                      <Link to={`/estate/${property.estate.id}`}>
                        View Estate
                      </Link>
                    </div>
                  </div>

                  <p className="small text-muted mb-3">
                    {property.estate.description}
                  </p>

                  {property.estate.features && (
                    <div className="estate-features">
                      <h6 className="small fw-bold mb-2">Estate Features:</h6>
                      <div
                        className="row g-1 small"
                        dangerouslySetInnerHTML={createMarkup(
                          property.estate.features
                        )}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <InspectionRequestModal
        isOpen={showInspectionModal}
        onClose={() => setShowInspectionModal(false)}
        propertyId={property?.id}
      />
    </>
  );
};

export default PropertyDetails;
